import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import ColorPicker from '../../components/theme/ColorPicker';
import Avatar from '../../components/common/Avatar';
import ScheduleIndicator from '../../components/common/ScheduleIndicator';
import { ChristmasGarland, Snowflakes, HalloweenBats, FloatingPumpkins, SummerWaves, EasterEggs, AvatarDecoration } from '../../components/theme/SeasonalDecorations';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Palette, 
  Image, 
  Clock, 
  Sparkles,
  Upload,
  Sun,
  Moon,
  Save,
  RotateCcw,
  Eye,
  Loader2,
  Check,
  Rocket,
  Type
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const ThemeSettingsPage = () => {
  const { user } = useAuth();
  const { theme, updateColors, updateLogo, updateSchedule, updateSeasonal, updateTheme, reloadTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  
  // Local state for editing
  const [colors, setColors] = useState(theme.colors);
  const [logo, setLogo] = useState(theme.logo);
  const [copiloteLogo, setCopiloteLogo] = useState(theme.copiloteLogo || { lightUrl: '', darkUrl: '' });
  const [schedule, setSchedule] = useState(theme.schedule);
  const [seasonal, setSeasonal] = useState(theme.seasonal);
  
  // Extended colors for text and dark mode
  const [textColors, setTextColors] = useState({
    buttonText: theme.colors?.buttonText || '#1a365d',
    titleText: theme.colors?.titleText || '#1a365d',
    bodyText: theme.colors?.bodyText || '#334155',
  });
  
  const [copiloteColors, setCopiloteColors] = useState({
    bgPrimary: theme.copiloteColors?.bgPrimary || '#0f172a',
    bgSecondary: theme.copiloteColors?.bgSecondary || '#1e293b',
    bgCard: theme.copiloteColors?.bgCard || '#1e293b',
    textPrimary: theme.copiloteColors?.textPrimary || '#f8fafc',
    textSecondary: theme.copiloteColors?.textSecondary || '#94a3b8',
    border: theme.copiloteColors?.border || '#334155',
    accent: theme.copiloteColors?.accent || '#00d4aa',
  });

  // Sync local state when theme changes
  useEffect(() => {
    setColors(theme.colors);
    setLogo(theme.logo);
    setCopiloteLogo(theme.copiloteLogo || { lightUrl: '', darkUrl: '' });
    setSchedule(theme.schedule);
    setSeasonal(theme.seasonal);
    setTextColors({
      buttonText: theme.colors?.buttonText || '#1a365d',
      titleText: theme.colors?.titleText || '#1a365d',
      bodyText: theme.colors?.bodyText || '#334155',
    });
    setCopiloteColors({
      bgPrimary: theme.copiloteColors?.bgPrimary || '#0f172a',
      bgSecondary: theme.copiloteColors?.bgSecondary || '#1e293b',
      bgCard: theme.copiloteColors?.bgCard || '#1e293b',
      textPrimary: theme.copiloteColors?.textPrimary || '#f8fafc',
      textSecondary: theme.copiloteColors?.textSecondary || '#94a3b8',
      border: theme.copiloteColors?.border || '#334155',
      accent: theme.copiloteColors?.accent || '#00d4aa',
    });
  }, [theme]);

  const handleSaveColors = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const success = await updateColors(colors);
      if (success) {
        toast.success('Couleurs mises à jour !');
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLogo = async () => {
    setSaving(true);
    try {
      const success = await updateLogo(logo);
      if (success) {
        toast.success('Logo mis à jour !');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSchedule = async () => {
    setSaving(true);
    try {
      const success = await updateSchedule(schedule);
      if (success) {
        toast.success('Horaires mis à jour !');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSeasonal = async () => {
    setSaving(true);
    try {
      const success = await updateSeasonal(seasonal);
      if (success) {
        toast.success('Thème saisonnier mis à jour !');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleResetColors = () => {
    const defaultColors = {
      primary: '#1a365d',
      primaryLight: '#2a4a7f',
      primaryDark: '#0f2342',
      secondary: '#2a4a7f',
      accent: '#00d4aa',
      accentHover: '#00b38f',
      accentLight: '#e6fbf6',
      alert: '#f59e0b',
      alertLight: '#fef3c7',
    };
    setColors(defaultColors);
    toast.info('Couleurs réinitialisées (cliquez Enregistrer pour appliquer)');
  };

  // Helper function to lighten/darken a color
  const adjustColor = (hex, percent) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.min(255, Math.max(0, (num >> 16) + amt));
    const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amt));
    const B = Math.min(255, Math.max(0, (num & 0x0000FF) + amt));
    return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
  };

  const handleFileUpload = async (e, type, logoType = 'main') => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/png', 'image/svg+xml', 'image/jpeg', 'image/webp', 'application/json'];
    if (!validTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez PNG, SVG, JPG, WebP ou JSON (Lottie).');
      return;
    }
    
    // Convert to base64 for now (in production, you'd upload to a server)
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      if (logoType === 'copilote') {
        if (type === 'light') {
          setCopiloteLogo({ ...copiloteLogo, lightUrl: base64 });
        } else {
          setCopiloteLogo({ ...copiloteLogo, darkUrl: base64 });
        }
      } else {
        if (type === 'light') {
          setLogo({ ...logo, lightUrl: base64 });
        } else {
          setLogo({ ...logo, darkUrl: base64 });
        }
      }
      toast.success('Image chargée ! Cliquez Enregistrer pour appliquer.');
    };
    reader.readAsDataURL(file);
  };

  const handleSaveCopiloteLogo = async () => {
    setSaving(true);
    try {
      await updateTheme({ copiloteLogo });
      toast.success('Logo Copilote mis à jour !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const dayLabels = {
    monday: 'Lundi',
    tuesday: 'Mardi',
    wednesday: 'Mercredi',
    thursday: 'Jeudi',
    friday: 'Vendredi',
    saturday: 'Samedi',
    sunday: 'Dimanche',
  };

  const seasonalOptions = [
    { value: 'none', label: 'Aucun', icon: '🚫' },
    { value: 'christmas', label: 'Noël', icon: '🎄' },
    { value: 'halloween', label: 'Halloween', icon: '🎃' },
    { value: 'summer', label: 'Été', icon: '☀️' },
    { value: 'easter', label: 'Pâques', icon: '🐰' },
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold" style={{ color: colors.primary }} data-testid="settings-title">
            Personnalisation
          </h1>
          <p className="text-[#64748b] mt-1">
            Personnalisez l'apparence de votre plateforme
          </p>
        </div>

        <Tabs defaultValue="colors" className="space-y-6">
          <TabsList className="bg-white border border-[#e2e8f0] p-1 rounded-xl flex-wrap">
            <TabsTrigger 
              value="colors" 
              className="data-[state=active]:text-white rounded-lg"
              style={{ backgroundColor: 'transparent' }}
              data-state-active-style={{ backgroundColor: colors.accent, color: colors.primary }}
            >
              <Palette className="w-4 h-4 mr-2" />
              Couleurs
            </TabsTrigger>
            <TabsTrigger value="text" className="rounded-lg">
              <Type className="w-4 h-4 mr-2" />
              Textes
            </TabsTrigger>
            <TabsTrigger value="logo" className="rounded-lg">
              <Image className="w-4 h-4 mr-2" />
              Logo & Avatar
            </TabsTrigger>
            <TabsTrigger value="schedule" className="rounded-lg">
              <Clock className="w-4 h-4 mr-2" />
              Horaires
            </TabsTrigger>
          </TabsList>

          {/* Colors Tab */}
          <TabsContent value="colors">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="card lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="w-5 h-5" style={{ color: colors.accent }} />
                    Couleurs du thème
                  </CardTitle>
                  <CardDescription>
                    Définissez les 3 couleurs principales de votre application
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Main 3 colors - simplified */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-3">
                      <h4 className="font-semibold text-sm" style={{ color: colors.primary }}>Couleur Primaire</h4>
                      <p className="text-xs text-slate-500">Header, sidebar, titres</p>
                      <ColorPicker
                        label=""
                        showLabel={false}
                        color={colors.primary}
                        onChange={(c) => setColors({ ...colors, primary: c, primaryDark: adjustColor(c, -20), primaryLight: adjustColor(c, 20) })}
                      />
                    </div>
                    <div className="space-y-3">
                      <h4 className="font-semibold text-sm" style={{ color: colors.primary }}>Couleur Secondaire</h4>
                      <p className="text-xs text-slate-500">Boutons secondaires, badges</p>
                      <ColorPicker
                        label=""
                        showLabel={false}
                        color={colors.secondary || colors.primaryLight}
                        onChange={(c) => setColors({ ...colors, secondary: c, primaryLight: c })}
                      />
                    </div>
                    <div className="space-y-3">
                      <h4 className="font-semibold text-sm" style={{ color: colors.primary }}>Couleur d'Accent</h4>
                      <p className="text-xs text-slate-500">Boutons CTA, liens actifs</p>
                      <ColorPicker
                        label=""
                        showLabel={false}
                        color={colors.accent}
                        onChange={(c) => setColors({ ...colors, accent: c, accentHover: adjustColor(c, -15), accentLight: adjustColor(c, 40) })}
                      />
                    </div>
                  </div>
                  
                  {/* Preview section */}
                  <div className="pt-4 border-t border-slate-200">
                    <h4 className="font-semibold text-sm mb-4" style={{ color: colors.primary }}>Aperçu</h4>
                    <div className="flex flex-wrap gap-4 items-center">
                      <div className="px-4 py-2 rounded-lg text-white text-sm font-medium" style={{ backgroundColor: colors.primary }}>
                        Primaire
                      </div>
                      <div className="px-4 py-2 rounded-lg text-white text-sm font-medium" style={{ backgroundColor: colors.secondary || colors.primaryLight }}>
                        Secondaire
                      </div>
                      <div className="px-4 py-2 rounded-lg text-sm font-medium" style={{ backgroundColor: colors.accent, color: colors.primary }}>
                        Accent
                      </div>
                      <div className="px-4 py-2 rounded-lg border text-sm" style={{ borderColor: colors.primary, color: colors.primary }}>
                        Outline
                      </div>
                    </div>
                  </div>
                  
                  {/* Advanced colors - collapsible */}
                  <details className="pt-4 border-t border-slate-200">
                    <summary className="cursor-pointer text-sm font-medium text-slate-600 hover:text-slate-800 mb-4">
                      Options avancées (couleurs complémentaires)
                    </summary>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                      <ColorPicker
                        label="Primaire claire"
                        color={colors.primaryLight}
                        onChange={(c) => setColors({ ...colors, primaryLight: c })}
                      />
                      <ColorPicker
                        label="Primaire foncée"
                        color={colors.primaryDark}
                        onChange={(c) => setColors({ ...colors, primaryDark: c })}
                      />
                      <ColorPicker
                        label="Accent hover"
                        color={colors.accentHover}
                        onChange={(c) => setColors({ ...colors, accentHover: c })}
                      />
                      <ColorPicker
                        label="Accent claire"
                        color={colors.accentLight}
                        onChange={(c) => setColors({ ...colors, accentLight: c })}
                      />
                      <ColorPicker
                        label="Alerte (Orange)"
                        color={colors.alert}
                        onChange={(c) => setColors({ ...colors, alert: c })}
                      />
                      <ColorPicker
                        label="Alerte claire"
                        color={colors.alertLight}
                        onChange={(c) => setColors({ ...colors, alertLight: c })}
                      />
                    </div>
                  </details>
                  
                  <div className="flex justify-between pt-4 border-t border-[#e2e8f0]">
                    <Button variant="outline" onClick={handleResetColors}>
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Réinitialiser
                    </Button>
                    <Button 
                      onClick={handleSaveColors} 
                      disabled={saving}
                      style={{ backgroundColor: colors.accent, color: colors.primary }}
                      className="font-bold"
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : saved ? (
                        <Check className="w-4 h-4 mr-2" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      {saved ? 'Enregistré !' : 'Enregistrer'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Preview */}
              <Card className="card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    Aperçu en direct
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div 
                    className="p-4 rounded-xl text-white"
                    style={{ backgroundColor: colors.primary }}
                  >
                    <p className="font-semibold">Couleur principale</p>
                    <p className="text-sm opacity-80">{colors.primary}</p>
                  </div>
                  
                  <Button 
                    className="w-full font-bold rounded-full"
                    style={{ backgroundColor: colors.accent, color: colors.primary }}
                  >
                    Bouton action
                  </Button>
                  
                  <div 
                    className="p-3 rounded-lg border"
                    style={{ backgroundColor: colors.alertLight, borderColor: colors.alert }}
                  >
                    <p className="text-sm font-medium" style={{ color: colors.alert }}>
                      ⚠️ Message d'alerte
                    </p>
                  </div>
                  
                  <div className="flex gap-2">
                    <div 
                      className="flex-1 h-8 rounded"
                      style={{ backgroundColor: colors.primary }}
                    />
                    <div 
                      className="flex-1 h-8 rounded"
                      style={{ backgroundColor: colors.accent }}
                    />
                    <div 
                      className="flex-1 h-8 rounded"
                      style={{ backgroundColor: colors.alert }}
                    />
                  </div>
                  
                  <div className="pt-4 border-t">
                    <p className="text-xs text-[#64748b] mb-2">Sidebar preview</p>
                    <div 
                      className="p-3 rounded-lg"
                      style={{ backgroundColor: colors.primary }}
                    >
                      <div className="flex items-center gap-2 text-white/80 text-sm">
                        <div 
                          className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold"
                          style={{ backgroundColor: colors.accent, color: colors.primary }}
                        >
                          M
                        </div>
                        <span>Copilote MSC</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Text Colors Tab */}
          <TabsContent value="text">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="card lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Type className="w-5 h-5" style={{ color: colors.accent }} />
                    Couleurs des textes
                  </CardTitle>
                  <CardDescription>
                    Personnalisez les couleurs des titres, boutons et textes
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <ColorPicker
                      label="Texte des boutons"
                      color={textColors.buttonText}
                      onChange={(c) => setTextColors({ ...textColors, buttonText: c })}
                    />
                    <ColorPicker
                      label="Couleur des titres"
                      color={textColors.titleText}
                      onChange={(c) => setTextColors({ ...textColors, titleText: c })}
                    />
                    <ColorPicker
                      label="Texte du corps"
                      color={textColors.bodyText}
                      onChange={(c) => setTextColors({ ...textColors, bodyText: c })}
                    />
                  </div>
                  
                  <div className="flex justify-between pt-4 border-t border-[#e2e8f0]">
                    <Button variant="outline" onClick={() => setTextColors({
                      buttonText: '#1a365d',
                      titleText: '#1a365d',
                      bodyText: '#334155',
                    })}>
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Réinitialiser
                    </Button>
                    <Button 
                      onClick={async () => {
                        setSaving(true);
                        const newColors = { ...colors, ...textColors };
                        await updateColors(newColors);
                        setSaving(false);
                        toast.success('Couleurs de texte mises à jour !');
                      }} 
                      disabled={saving}
                      style={{ backgroundColor: colors.accent, color: textColors.buttonText }}
                      className="font-bold"
                    >
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Enregistrer
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Text Preview */}
              <Card className="card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    Aperçu
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h2 className="text-2xl font-bold mb-2" style={{ color: textColors.titleText }}>
                      Titre exemple
                    </h2>
                    <p style={{ color: textColors.bodyText }}>
                      Voici un exemple de texte de corps avec la couleur sélectionnée.
                    </p>
                  </div>
                  
                  <Button 
                    className="w-full font-bold rounded-full"
                    style={{ backgroundColor: colors.accent, color: textColors.buttonText }}
                  >
                    Bouton action
                  </Button>
                  
                  <Button variant="outline" className="w-full font-bold rounded-full">
                    Bouton secondaire
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Logo Tab */}
          <TabsContent value="logo">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Logo principal fond clair */}
              <Card className="card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sun className="w-5 h-5" />
                    Logo fond clair
                  </CardTitle>
                  <CardDescription>
                    Logo utilisé sur les fonds blancs/clairs
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="border-2 border-dashed border-[#e2e8f0] rounded-xl p-8 bg-white text-center">
                    {logo.lightUrl ? (
                      <img 
                        src={logo.lightUrl} 
                        alt="Logo clair" 
                        className="max-h-20 mx-auto rounded-full object-cover"
                        style={{ width: '80px', height: '80px' }}
                      />
                    ) : (
                      <div 
                        className="w-20 h-20 mx-auto rounded-full flex items-center justify-center"
                        style={{ backgroundColor: colors.primary }}
                      >
                        <span style={{ color: colors.accent }} className="font-bold text-2xl font-mono">MSC</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label>URL du logo (fond clair)</Label>
                    <Input
                      value={logo.lightUrl || ''}
                      onChange={(e) => setLogo({ ...logo, lightUrl: e.target.value })}
                      placeholder="https://... ou collez une image base64"
                      className="input-default"
                    />
                  </div>
                  
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      onChange={(e) => handleFileUpload(e, 'light', 'main')}
                      className="hidden"
                      accept="image/png,image/svg+xml,image/jpeg,image/webp,application/json"
                    />
                    <div className="flex items-center justify-center gap-2 p-3 border border-[#e2e8f0] rounded-lg hover:bg-[#f8fafc] transition-colors">
                      <Upload className="w-4 h-4" />
                      <span>Télécharger (PNG, SVG, JPG, JSON)</span>
                    </div>
                  </label>
                </CardContent>
              </Card>

              {/* Logo principal fond sombre */}
              <Card className="card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Moon className="w-5 h-5" />
                    Logo fond sombre
                  </CardTitle>
                  <CardDescription>
                    Logo utilisé sur les fonds sombres (sidebar)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div 
                    className="border-2 border-dashed border-[#e2e8f0] rounded-xl p-8 text-center"
                    style={{ backgroundColor: colors.primary }}
                  >
                    {logo.darkUrl ? (
                      <img 
                        src={logo.darkUrl} 
                        alt="Logo sombre" 
                        className="max-h-20 mx-auto rounded-full object-cover"
                        style={{ width: '80px', height: '80px' }}
                      />
                    ) : (
                      <div 
                        className="w-20 h-20 mx-auto rounded-full flex items-center justify-center"
                        style={{ backgroundColor: colors.accent }}
                      >
                        <span style={{ color: colors.primary }} className="font-bold text-2xl font-mono">MSC</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label>URL du logo (fond sombre)</Label>
                    <Input
                      value={logo.darkUrl || ''}
                      onChange={(e) => setLogo({ ...logo, darkUrl: e.target.value })}
                      placeholder="https://... ou collez une image base64"
                      className="input-default"
                    />
                  </div>
                  
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      onChange={(e) => handleFileUpload(e, 'dark', 'main')}
                      className="hidden"
                      accept="image/png,image/svg+xml,image/jpeg,image/webp,application/json"
                    />
                    <div className="flex items-center justify-center gap-2 p-3 border border-[#e2e8f0] rounded-lg hover:bg-[#f8fafc] transition-colors">
                      <Upload className="w-4 h-4" />
                      <span>Télécharger (PNG, SVG, JPG, JSON)</span>
                    </div>
                  </label>
                </CardContent>
              </Card>

              {/* Logo Copilote fond clair */}
              <Card className="card border-orange-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center">
                      <Rocket className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-base">Logo Copilote (fond clair)</CardTitle>
                      <CardDescription className="text-xs">
                        Logo affiché en mode Copilote quand scrollé
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="border-2 border-dashed border-orange-200 rounded-xl p-6 bg-white text-center">
                    {copiloteLogo.lightUrl ? (
                      <img 
                        src={copiloteLogo.lightUrl} 
                        alt="Logo Copilote clair" 
                        className="max-h-16 mx-auto object-contain"
                      />
                    ) : (
                      <div className="flex flex-col items-center">
                        <span className="font-black text-2xl bg-gradient-to-r from-orange-500 via-amber-400 to-yellow-400 bg-clip-text text-transparent">
                          MSC
                        </span>
                        <span className="text-xs font-semibold text-orange-500 -mt-1 tracking-wider">
                          Copilote
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs">URL du logo Copilote (fond clair)</Label>
                    <Input
                      value={copiloteLogo.lightUrl || ''}
                      onChange={(e) => setCopiloteLogo({ ...copiloteLogo, lightUrl: e.target.value })}
                      placeholder="https://... ou collez une image base64"
                      className="input-default text-sm"
                    />
                  </div>
                  
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      onChange={(e) => handleFileUpload(e, 'light', 'copilote')}
                      className="hidden"
                      accept="image/png,image/svg+xml,image/jpeg,image/webp,application/json"
                    />
                    <div className="flex items-center justify-center gap-2 p-2 border border-orange-200 rounded-lg hover:bg-orange-50 transition-colors text-sm">
                      <Upload className="w-4 h-4 text-orange-500" />
                      <span className="text-orange-600">Télécharger (PNG, SVG, JSON)</span>
                    </div>
                  </label>
                </CardContent>
              </Card>

              {/* Logo Copilote fond sombre */}
              <Card className="card border-orange-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center">
                      <Moon className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-base">Logo Copilote (fond sombre)</CardTitle>
                      <CardDescription className="text-xs">
                        Logo affiché en mode Copilote sur fond sombre
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="border-2 border-dashed border-orange-200 rounded-xl p-6 bg-slate-900 text-center">
                    {copiloteLogo.darkUrl ? (
                      <img 
                        src={copiloteLogo.darkUrl} 
                        alt="Logo Copilote sombre" 
                        className="max-h-16 mx-auto object-contain"
                      />
                    ) : (
                      <div className="flex flex-col items-center">
                        <span className="font-black text-2xl text-white">
                          MSC
                        </span>
                        <span className="text-xs font-semibold text-white/80 -mt-1 tracking-wider">
                          Copilote
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs">URL du logo Copilote (fond sombre)</Label>
                    <Input
                      value={copiloteLogo.darkUrl || ''}
                      onChange={(e) => setCopiloteLogo({ ...copiloteLogo, darkUrl: e.target.value })}
                      placeholder="https://... ou collez une image base64"
                      className="input-default text-sm"
                    />
                  </div>
                  
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      onChange={(e) => handleFileUpload(e, 'dark', 'copilote')}
                      className="hidden"
                      accept="image/png,image/svg+xml,image/jpeg,image/webp,application/json"
                    />
                    <div className="flex items-center justify-center gap-2 p-2 border border-orange-200 rounded-lg hover:bg-orange-50 transition-colors text-sm">
                      <Upload className="w-4 h-4 text-orange-500" />
                      <span className="text-orange-600">Télécharger (PNG, SVG, JSON)</span>
                    </div>
                  </label>
                </CardContent>
              </Card>

              {/* Avatars preview + Save buttons */}
              <Card className="card lg:col-span-2">
                <CardHeader>
                  <CardTitle>Aperçu des avatars (tous en rond)</CardTitle>
                  <CardDescription>
                    Tous les avatars sont affichés en rond avec les décorations saisonnières
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-8 flex-wrap">
                    <div className="text-center">
                      <Avatar name="MSC Agence" size="xl" type="agency" />
                      <p className="text-xs text-[#64748b] mt-2">Agence</p>
                    </div>
                    <div className="text-center">
                      <Avatar name="Jean Client" size="xl" type="client" />
                      <p className="text-xs text-[#64748b] mt-2">Client</p>
                    </div>
                    <div className="text-center">
                      <Avatar name="Pierre Designer" size="xl" type="creative" />
                      <p className="text-xs text-[#64748b] mt-2">Créatif</p>
                    </div>
                    <div className="text-center">
                      <Avatar name="Marie User" size="xl" type="user" />
                      <p className="text-xs text-[#64748b] mt-2">Utilisateur</p>
                    </div>
                  </div>
                  
                  <div className="flex justify-between mt-6 pt-4 border-t border-[#e2e8f0]">
                    <Button 
                      onClick={handleSaveCopiloteLogo} 
                      disabled={saving}
                      className="font-bold bg-gradient-to-r from-orange-500 to-amber-400 text-white"
                    >
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rocket className="w-4 h-4 mr-2" />}
                      Enregistrer Logo Copilote
                    </Button>
                    <Button 
                      onClick={handleSaveLogo} 
                      disabled={saving}
                      style={{ backgroundColor: colors.accent, color: colors.primary }}
                      className="font-bold"
                    >
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Enregistrer Logo Principal
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="card lg:col-span-2">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Clock className="w-5 h-5" style={{ color: colors.accent }} />
                        Horaires d'ouverture
                      </CardTitle>
                      <CardDescription>
                        Définissez vos horaires hebdomadaires
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor="schedule-enabled" className="text-sm">Activer</Label>
                      <Switch
                        id="schedule-enabled"
                        checked={schedule.enabled}
                        onCheckedChange={(checked) => setSchedule({ ...schedule, enabled: checked })}
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {Object.entries(schedule.regular || {}).map(([day, hours]) => (
                    <div key={day} className="flex items-center gap-4 p-3 rounded-lg bg-[#f8fafc]">
                      <div className="w-24">
                        <span className="font-medium" style={{ color: colors.primary }}>
                          {dayLabels[day]}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={!hours.closed}
                          onCheckedChange={(checked) => setSchedule({
                            ...schedule,
                            regular: {
                              ...schedule.regular,
                              [day]: { ...hours, closed: !checked }
                            }
                          })}
                        />
                        <span className="text-sm text-[#64748b]">
                          {hours.closed ? 'Fermé' : 'Ouvert'}
                        </span>
                      </div>
                      
                      {!hours.closed && (
                        <>
                          <Input
                            type="time"
                            value={hours.open}
                            onChange={(e) => setSchedule({
                              ...schedule,
                              regular: {
                                ...schedule.regular,
                                [day]: { ...hours, open: e.target.value }
                              }
                            })}
                            className="input-default w-28"
                          />
                          <span className="text-[#64748b]">à</span>
                          <Input
                            type="time"
                            value={hours.close}
                            onChange={(e) => setSchedule({
                              ...schedule,
                              regular: {
                                ...schedule.regular,
                                [day]: { ...hours, close: e.target.value }
                              }
                            })}
                            className="input-default w-28"
                          />
                        </>
                      )}
                    </div>
                  ))}
                  
                  <div className="flex justify-end pt-4 border-t border-[#e2e8f0]">
                    <Button 
                      onClick={handleSaveSchedule} 
                      disabled={saving}
                      style={{ backgroundColor: colors.accent, color: colors.primary }}
                      className="font-bold"
                    >
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Enregistrer
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="card">
                <CardHeader>
                  <CardTitle>Aperçu en direct</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ScheduleIndicator />
                  
                  <div className="pt-4 border-t border-[#e2e8f0]">
                    <p className="text-sm text-[#64748b] mb-2">Version compacte (header)</p>
                    <ScheduleIndicator compact />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ThemeSettingsPage;
